import { atom } from 'recoil';
import { selector, useRecoilCallback } from 'recoil';
import storage from '~/storage';
import { fetchProfile } from '../Profile/api';
import { User } from '@restaurant/types';

export const authState = atom<{
  isLoggedIn: boolean | null;
  user: User | null;
}>({
  key: 'auth',
  default: {
    isLoggedIn: null,
    user: null,
  },
});

const AUTH = 'auth';

let _token: string | null = null;

export const useIsLoggedIn = () => {
  return useRecoilCallback(
    ({ snapshot, set }) =>
      async () => {
        const { isLoggedIn } = snapshot.getLoadable(authState).valueOrThrow();
        if (isLoggedIn != null) {
          return isLoggedIn;
        }

        const token = storage.get(AUTH, null);
        if (!token) {
          return false;
        }

        _token = token;
        try {
          const { data } = await fetchProfile();
          set(authState, { isLoggedIn: true, user: data });
          return true;
        } catch (e) {
          _token = null;
          storage.remove(AUTH);
          return false;
        }
      },
    []
  );
};

export const userIdSelector = selector({
  key: 'userIdSelector',
  get: ({ get }) => {
    return get(authState).user?.id;
  },
});

export const userSelector = selector({
  key: 'userSelector',
  get: ({ get }) => {
    return get(authState).user;
  },
});

export const useSaveLogin = () => {
  return useRecoilCallback(
    ({ set }) =>
      ({ token, user }: { token: string; user: User }) => {
        set(authState, {
          isLoggedIn: true,
          user,
        });

        storage.set(AUTH, token);
        _token = token;
      }
  );
};

export const useLogout = () => {
  return useRecoilCallback(({ set }) => () => {
    set(authState, {
      isLoggedIn: false,
      user: null,
    });
    storage.clear();
    _token = null;
    window.location.href = '/auth/login';
  });
};

const authStore = {
  getToken: () => _token,
};

export default authStore;
